import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import PublicNavHeader from '../components/PublicNavHeader';
import Footer from '../components/Footer';

import {
  ArrowRight,
  CalendarBlank,
  ChartBar,
  CheckCircle,
  Clock,
  Crosshair,
  Eye,
  GearSix,
  Lightning,
  Rocket,
  Sparkle,
  Target,
  UsersThree
} from '@phosphor-icons/react';

const TEAL = '#00E5C4';
const CORAL = '#FF6B6B';

const HowWeScore = () => {
  return (
    <div className="min-h-screen bg-[#0a0a0f]">
      <PublicNavHeader />

      {/* ───── 1. HERO ───── */}
      <section className="pt-32 pb-24 px-6 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div
            className="absolute top-0 left-1/4 w-[500px] h-[500px] rounded-full filter blur-[120px] opacity-[0.12]"
            style={{ background: TEAL }}
          />
          <div
            className="absolute bottom-0 right-1/4 w-[400px] h-[400px] rounded-full filter blur-[100px] opacity-[0.08]"
            style={{ background: CORAL }}
          />
        </div>

        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-6"
              style={{ background: `${TEAL}15`, border: `1px solid ${TEAL}30`, color: TEAL }}
            >
              <Rocket size={16} />
              Opportunity Engine
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight tracking-tight">
              Know Where to Start
              <span className="block" style={{ color: TEAL }}>
                - and What to Do Next
              </span>
            </h1>

            <p className="text-lg text-[#B0B8C4] max-w-2xl mx-auto mb-4 leading-relaxed">
              We analyse your business and show you exactly where AI workers can
              create the biggest impact first.
            </p>

            <p className="text-base text-[#7D8896] max-w-xl mx-auto mb-2 italic">
              This isn't a score. It's your action plan.
            </p>

            <p className="text-sm text-[#5C6778] max-w-xl mx-auto mb-10">
              Most businesses don't fail with AI - they just start in the wrong place.
            </p>

            <Link
              to="/register"
              className="relative overflow-hidden rounded-full group inline-flex"
              data-testid="hero-cta-btn"
            >
              <div
                className="absolute inset-0 rounded-full"
                style={{ background: TEAL }}
              />
              <span className="relative z-10 flex items-center justify-center gap-2 px-10 py-4 text-[#0B1120] font-semibold text-base">
                Start Free Assessment
                <ArrowRight size={20} weight="bold" />
              </span>
            </Link>

            <p className="text-xs text-[#5C6778] mt-4">
              No credit card required &middot; Takes 5-7 minutes &middot; Instant results
            </p>
          </motion.div>
        </div>
      </section>

      {/* ───── 2. YOUR BUSINESS ROADMAP ───── */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-14"
          >
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">
              Your Business Roadmap
            </h2>
            <p className="text-base text-[#7D8896] max-w-2xl mx-auto">
              Three dimensions that tell you exactly where to focus
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                Icon: Crosshair,
                title: 'Your Starting Point',
                body: 'How prepared your business is to implement AI workers.',
              },
              {
                Icon: Target,
                title: 'Where You Win First',
                body: 'Which opportunities deliver the fastest ROI.',
              },
              {
                Icon: Clock,
                title: 'How Fast You See Results',
                body: 'What can be implemented immediately vs later.',
              },
            ].map((card, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="rounded-2xl p-8 border transition-all hover:-translate-y-0.5"
                style={{
                  background: 'rgba(255,255,255,0.03)',
                  borderColor: `${TEAL}20`,
                }}
                data-testid={`roadmap-card-${i}`}
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-5"
                  style={{ background: `${TEAL}15` }}
                >
                  <card.Icon size={24} style={{ color: TEAL }} />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{card.title}</h3>
                <p className="text-sm text-[#7D8896] leading-relaxed">{card.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ───── 3. HOW READY YOUR BUSINESS IS ───── */}
      <section className="py-20 px-6 bg-[#0d0d14]">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">
                How Ready Your Business Is
              </h2>
              <p className="text-base text-[#7D8896] mb-8 leading-relaxed">
                A quick snapshot of how easily AI workers can be deployed in your business.
              </p>

              <p className="text-sm text-[#B0B8C4] mb-6">
                We look at a few key areas to understand how quickly you can start seeing results:
              </p>

              <div className="space-y-4 mb-8">
                {[
                  { Icon: ChartBar, label: 'Data & Tracking' },
                  { Icon: GearSix, label: 'Processes' },
                  { Icon: Lightning, label: 'Tools & Systems' },
                  { Icon: UsersThree, label: 'Team Setup' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ background: `${TEAL}15` }}
                    >
                      <item.Icon size={16} style={{ color: TEAL }} />
                    </div>
                    <span className="text-white text-sm font-medium">{item.label}</span>
                  </div>
                ))}
              </div>

              <div
                className="rounded-xl px-5 py-4 text-sm leading-relaxed"
                style={{ background: `${TEAL}08`, border: `1px solid ${TEAL}20` }}
              >
                <p className="text-[#B0B8C4]">
                  <span style={{ color: TEAL }} className="font-medium">
                    Low readiness doesn't block you
                  </span>{' '}
                  - it just means we start with simpler, faster wins first.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div
                className="rounded-2xl p-8 border"
                style={{ background: 'rgba(255,255,255,0.03)', borderColor: 'rgba(255,255,255,0.06)' }}
              >
                <p className="text-xs text-[#5C6778] uppercase tracking-widest mb-6">
                  Sample Readiness Snapshot
                </p>
                {[
                  { label: 'Data & Tracking', pct: 82, color: TEAL },
                  { label: 'Processes', pct: 68, color: TEAL },
                  { label: 'Tools & Systems', pct: 75, color: TEAL },
                  { label: 'Team Setup', pct: 55, color: CORAL },
                ].map((bar, i) => (
                  <div key={i} className="mb-5 last:mb-0">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-sm text-[#B0B8C4]">{bar.label}</span>
                      <span className="text-sm font-semibold" style={{ color: bar.color }}>
                        {bar.pct}%
                      </span>
                    </div>
                    <div className="w-full h-2 bg-[#1A1B23] rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        whileInView={{ width: `${bar.pct}%` }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, delay: i * 0.1 }}
                        className="h-full rounded-full"
                        style={{ background: bar.color }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ───── 4. WHERE YOU GET RESULTS FIRST (star) ───── */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-14"
          >
            <div
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-5 text-xs font-medium"
              style={{ background: `${TEAL}15`, color: TEAL }}
            >
              <Sparkle size={14} />
              Most Valuable Insight
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">
              Where You Get Results First
            </h2>
            <p className="text-base text-[#7D8896] max-w-2xl mx-auto">
              Not all opportunities are equal - we highlight the ones that create the
              biggest impact quickly.
            </p>
          </motion.div>

          {/* Scoring dimensions */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {[
              {
                title: 'Impact',
                body: 'How much this improves your business.',
                Icon: Lightning,
              },
              {
                title: 'Effort',
                body: 'How easy it is to implement.',
                Icon: GearSix,
              },
              {
                title: 'Confidence',
                body: 'How likely it is to work.',
                Icon: CheckCircle,
              },
            ].map((p, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="rounded-2xl p-7 border text-center"
                style={{ background: 'rgba(255,255,255,0.03)', borderColor: `${TEAL}15` }}
              >
                <div
                  className="w-11 h-11 rounded-xl flex items-center justify-center mx-auto mb-4"
                  style={{ background: `${TEAL}15` }}
                >
                  <p.Icon size={22} style={{ color: TEAL }} />
                </div>
                <h4 className="text-base font-semibold text-white mb-1">{p.title}</h4>
                <p className="text-sm text-[#7D8896]">{p.body}</p>
              </motion.div>
            ))}
          </div>

          {/* Priority labels */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="rounded-2xl p-8 border"
            style={{ background: 'rgba(255,255,255,0.02)', borderColor: 'rgba(255,255,255,0.06)' }}
          >
            <p className="text-xs text-[#5C6778] uppercase tracking-widest mb-6 text-center">
              How We Label Opportunities
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { label: 'Start Here', desc: 'Highest impact, easiest to implement', color: TEAL },
                { label: 'Strong Next Step', desc: 'High value, slightly more effort', color: TEAL },
                { label: 'Plan Later', desc: 'Good potential, needs preparation', color: '#7D8896' },
                { label: 'Needs More Setup', desc: 'Valuable but requires groundwork', color: CORAL },
              ].map((tier, i) => (
                <div
                  key={i}
                  className="rounded-xl px-5 py-4 border"
                  style={{ borderColor: `${tier.color}25`, background: `${tier.color}08` }}
                >
                  <span className="text-sm font-semibold block mb-1" style={{ color: tier.color }}>
                    {tier.label}
                  </span>
                  <span className="text-xs text-[#7D8896]">{tier.desc}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ───── 5. YOUR IMPLEMENTATION PLAN ───── */}
      <section className="py-20 px-6 bg-[#0d0d14]">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-14"
          >
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">
              Your Implementation Plan
            </h2>
            <p className="text-base text-[#7D8896] max-w-2xl mx-auto">
              We show you exactly what to do, and when to do it.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {[
              {
                phase: 'Start Now',
                window: '1-3 months',
                color: TEAL,
                desc: 'Quick wins you can implement immediately with minimal effort.',
                Icon: Rocket,
              },
              {
                phase: 'Build Next',
                window: '3-6 months',
                color: TEAL,
                desc: 'Stronger systems that build on early results.',
                Icon: GearSix,
              },
              {
                phase: 'Scale Later',
                window: '6-12 months',
                color: CORAL,
                desc: 'Transformational projects that scale what already works.',
                Icon: CalendarBlank,
              },
            ].map((p, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.12 }}
                className="rounded-2xl p-7 border relative overflow-hidden"
                style={{
                  background: 'rgba(255,255,255,0.03)',
                  borderColor: `${p.color}20`,
                }}
              >
                <div
                  className="absolute top-0 left-0 right-0 h-[3px]"
                  style={{ background: p.color }}
                />
                <div className="flex items-start gap-4">
                  <div
                    className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: `${p.color}15` }}
                  >
                    <p.Icon size={22} style={{ color: p.color }} />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-white mb-0.5">{p.phase}</h3>
                    <span className="text-xs" style={{ color: p.color }}>
                      {p.window}
                    </span>
                    <p className="text-sm text-[#7D8896] mt-2 leading-relaxed">{p.desc}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center text-sm text-[#5C6778] italic"
          >
            This ensures you get results early - not just plan for the future.
          </motion.p>
        </div>
      </section>

      {/* ───── 6. WHAT THIS MEANS FOR YOU ───── */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-14"
          >
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">
              What This Means for You
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                Icon: Rocket,
                title: 'Start with results, not complexity',
                body: "You don't need everything perfect to begin.",
                color: TEAL,
              },
              {
                Icon: Eye,
                title: 'Nothing is hidden',
                body: 'All opportunities are visible - even if scheduled later.',
                color: TEAL,
              },
              {
                Icon: CalendarBlank,
                title: 'Focus on timing, not importance',
                body: 'We prioritise what works now, not just what sounds good.',
                color: TEAL,
              },
            ].map((card, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="rounded-2xl p-7 border"
                style={{
                  background: 'rgba(255,255,255,0.03)',
                  borderColor: `${card.color}15`,
                }}
                data-testid={`takeaway-card-${i}`}
              >
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center mb-4"
                  style={{ background: `${card.color}12` }}
                >
                  <card.Icon size={20} style={{ color: card.color }} />
                </div>
                <h3 className="text-base font-semibold text-white mb-2">{card.title}</h3>
                <p className="text-sm text-[#7D8896] leading-relaxed">{card.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ───── 7. FINAL CTA ───── */}
      <section className="py-24 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="rounded-3xl p-12 border relative overflow-hidden"
            style={{
              background: 'rgba(255,255,255,0.03)',
              borderColor: `${TEAL}15`,
            }}
          >
            <div
              className="absolute -top-20 left-1/2 -translate-x-1/2 w-72 h-72 rounded-full filter blur-[100px] opacity-[0.12] pointer-events-none"
              style={{ background: TEAL }}
            />

            <div className="relative z-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
                Get Your AI Workforce Plan
              </h2>
              <p className="text-base text-[#7D8896] mb-10 max-w-lg mx-auto">
                See exactly where AI workers can create impact in your business - and
                how to start.
              </p>

              <Link
                to="/register"
                className="relative overflow-hidden rounded-full group inline-flex"
                data-testid="final-cta-btn"
              >
                <div
                  className="absolute inset-0 rounded-full"
                  style={{ background: TEAL }}
                />
                <span className="relative z-10 flex items-center justify-center gap-2 px-12 py-5 text-[#0B1120] font-semibold text-base">
                  Start Free Assessment
                  <ArrowRight size={20} weight="bold" />
                </span>
              </Link>

              <p className="text-xs text-[#5C6778] mt-5">
                No commitment required &middot; See your results in minutes.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HowWeScore;
